import { useEffect, useMemo, useRef, useState } from "react";
import { Row, Table, Container, Button, Col, Form, InputGroup } from "react-bootstrap";
import { useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import EditModal from "./EditModal";
import ImportModal from "./ImportModal";
import RemoveModal from "./RemoveModal";
import toastService from "../../_services/toastService";
import SatAPI from "../../_services/sat";
import { getFetchParamGenerator, getApiURL } from "../../_services/sat";
import { downloadBlob } from "../../_helpers/excel";
import { sortProgrammesLikeOpleidingenPage, isShownInOpleidingenFilter } from "../../_helpers/sortProgrammes";
import { loginRequest } from "../../_azure/authConfig";

const api = SatAPI();
const OPO_DIFF_SNAPSHOT_STORAGE_KEY = "SAT-OpoDiff-SnapshotName";
const OPO_DIFF_OPEN_STORAGE_KEY = "SAT-OpoDiff-Open";

function OPOs() {
  const { instance } = useMsal();
  const snapshot = useSelector(state => state.snapshot.fileName);

  const [opos, setOPOs] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [parallels, setParallels] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [diffSnapshotName, setDiffSnapshotName] = useState("");
  const [runningDiff, setRunningDiff] = useState(false);
  const [opoDiffData, setOpoDiffData] = useState(null);
  const [isDiffOpen, setIsDiffOpen] = useState(false);
  const [availableSnapshots, setAvailableSnapshots] = useState([]);
  const [highlightedOpoID, setHighlightedOpoID] = useState(null);
  const [modal, setModal] = useState(null);
  const [removeModal, setRemoveModal] = useState(null);
  const opoRowRefs = useRef({});
  const diffDetailsContainerRef = useRef(null);

  // filter
  const [selectedProgrammeIDs, setSelectedProgrammeIDs] = useState([]);

  const loadSnapshots = async () => {
    try {
      const snapshots = await api.getSnapshots();
      const snapshotNames = [...new Set(
        (snapshots || [])
          .map(s => (s.fileName || s.name || "").trim())
          .filter(Boolean),
      )].sort((a, b) => b.localeCompare(a));

      setAvailableSnapshots(snapshotNames);
      return snapshotNames;
    } catch {
      setAvailableSnapshots([]);
      return [];
    }
  };

  const snapshotExists = async snapshotName => {
    const snapshots = availableSnapshots.length > 0 ? availableSnapshots : await loadSnapshots();
    return snapshots.includes(snapshotName);
  };

  const setSelectedSnapshotName = snapshotName => {
    setDiffSnapshotName(snapshotName);
    localStorage.setItem(OPO_DIFF_SNAPSHOT_STORAGE_KEY, snapshotName);
  };

  const clearSelectedSnapshotName = () => {
    setDiffSnapshotName("");
    localStorage.removeItem(OPO_DIFF_SNAPSHOT_STORAGE_KEY);
  };

  useEffect(() => {
    if (diffSnapshotName) {
      localStorage.setItem(OPO_DIFF_SNAPSHOT_STORAGE_KEY, diffSnapshotName);
    } else {
      localStorage.removeItem(OPO_DIFF_SNAPSHOT_STORAGE_KEY);
    }
  }, [diffSnapshotName]);

  useEffect(() => {
    localStorage.setItem(OPO_DIFF_OPEN_STORAGE_KEY, isDiffOpen ? "true" : "false");
  }, [isDiffOpen]);

  const setDiffOpenState = isOpen => {
    setIsDiffOpen(isOpen);
    localStorage.setItem(OPO_DIFF_OPEN_STORAGE_KEY, isOpen ? "true" : "false");
  };

  const collapseAllDiffDetails = () => {
    const container = diffDetailsContainerRef.current;
    if (!container) {
      return;
    }

    container.querySelectorAll("details[open]").forEach(detail => detail.removeAttribute("open"));
  };

  const jumpToOpoRow = opoID => {
    const normalizedOpoID = `${opoID}`;
    const row = opoRowRefs.current[normalizedOpoID];

    if (!row) {
      toastService.send({ title: "Rij niet zichtbaar", message: "De overeenkomstige OPO-rij werd niet gevonden in de huidige tabelweergave." });
      return;
    }

    if (row.classList.contains("d-none")) {
      toastService.send({ title: "Rij gefilterd", message: "De rij is momenteel weggefilterd. Pas eerst de filters aan." });
      return;
    }

    row.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedOpoID(normalizedOpoID);
    setTimeout(() => setHighlightedOpoID(null), 1800);
  };

  const openEditModalFromDiff = opoID => {
    const opo = opos.find(item => `${item.ID ?? item.id}` === `${opoID}`);

    if (!opo) {
      toastService.send({ title: "OPO niet gevonden", message: "Deze OPO kon niet geopend worden voor bewerken." });
      return;
    }

    openModal(opo);
  };

  const handleAuthFailure = response => {
    if (response.status === 401 || response.status === 403) {
      toastService.send({
        title: "Inloggen vereist",
        message: "Je sessie is verlopen of je hebt geen toegang meer. Log opnieuw in en probeer het nog eens.",
      });
      instance.loginRedirect({ ...loginRequest, prompt: "create" }).catch(() => {});
      return true;
    }

    return false;
  };

  const mapDiffFieldNameToLabel = fieldName => {
    const fieldLabels = {
      zCode: "Z-code",
      z_code: "Z-code",
      name: "Naam",
      comments: "Opmerkingen",
      programmeID: "Opleiding",
      programme_id: "Opleiding",
      semester: "Semester",
      ectsCredits: "Studiepunten SAT",
      ects_credits: "Studiepunten SAT",
      realECTSCredits: "Studiepunten ECTS fiche",
      real_ects_credits: "Studiepunten ECTS fiche",
      contactQ1: "Contact Q1",
      contactQ2: "Contact Q2",
      contactQ3: "Contact Q3",
      contactQ4: "Contact Q4",
      contact_q1: "Contact Q1",
      contact_q2: "Contact Q2",
      contact_q3: "Contact Q3",
      contact_q4: "Contact Q4",
      internship: "Stage",
      stage: "Stage",
      internshipPercentage: "Stagepercentage",
      internship_percentage: "Stagepercentage",
      teachersPerClass: "Docenten per klas",
      teachers_per_class: "Docenten per klas",
      planPerPhase: "Plan per fase",
      plan_per_phase: "Plan per fase",
      opoID: "OPO",
      opo_id: "OPO",
      phaseID: "Fase",
      phase_id: "Fase",
      olaID: "OLA",
      ola_id: "OLA",
      surplus: "Extra studenten",
      numberOfClassesOverride: "Klassen",
      number_of_classes_override: "Klassen",
      semesterOverride: "Semester override",
      semester_override: "Semester override",
      ectsCreditsOverride: "SAT studiepunten override",
      ects_credits_override: "SAT studiepunten override",
      realEctsCreditsOverride: "ECTS studiepunten override",
      real_ects_credits_override: "ECTS studiepunten override",
      contactQ1Override: "Contact Q1 override",
      contactQ2Override: "Contact Q2 override",
      contactQ3Override: "Contact Q3 override",
      contactQ4Override: "Contact Q4 override",
      contact_q1_override: "Contact Q1 override",
      contact_q2_override: "Contact Q2 override",
      contact_q3_override: "Contact Q3 override",
      contact_q4_override: "Contact Q4 override",
      opoZCodeOverride: "OPO z-code override",
      opo_z_code_override: "OPO z-code override",
      olaZCodeOverride: "OLA z-code override",
      ola_z_code_override: "OLA z-code override",
      comment: "Commentaar",
      teachersPerClassOverride: "Docenten per klas override",
      teachers_per_class_override: "Docenten per klas override",
      tags: "Tags",
    };

    return fieldLabels[fieldName] || fieldName;
  };

  const formatDiffValue = value => {
    if (value === null || value === undefined || value === "") {
      return "(leeg)";
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return "(leeg)";
      }

      const names = value
        .map(item => {
          if (item === null || item === undefined) {
            return "";
          }

          if (typeof item === "string" || typeof item === "number") {
            return `${item}`;
          }

          return item.name || item.text || "";
        })
        .map(name => `${name}`.trim())
        .filter(Boolean);

      if (names.length > 0) {
        return names.join(", ");
      }
    }

    if (typeof value === "boolean") {
      return value ? "Ja" : "Nee";
    }

    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }

    return `${value}`;
  };

  const formatDiffFieldValue = (fieldName, value, item) => {
    if (fieldName === "programmeID" || fieldName === "programme_id") {
      const numericId = Number(value);
      if (Number.isFinite(numericId)) {
        const programme = (programmes || []).find(p => Number(p?.ID ?? p?.id) === numericId);
        if (programme?.name) {
          return programme.name;
        }
      }
    }

    if (fieldName === "opoID" || fieldName === "opo_id") {
      const numericId = Number(value);
      if (Number.isFinite(numericId)) {
        const opo = (opos || []).find(o => Number(o?.ID ?? o?.id) === numericId);
        if (opo?.name) {
          return opo.name;
        }
      }

      return item?.opoName || formatDiffValue(value);
    }

    return formatDiffValue(value);
  };

  const buildDiffItemDisplayName = item => {
    if (item.entityType === "OPO") {
      return item.opoName || item.opoZCode || item.key || `OPO #${item.id ?? "?"}`;
    }

    if (item.entityType === "OLA") {
      const olaText = item.olaName || item.key || `OLA #${item.id ?? "?"}`;
      return item.opoName ? `${item.opoName} - ${olaText}` : olaText;
    }

    const phaseText = item.phaseName || item.key || `Fasekoppeling #${item.id ?? "?"}`;
    const context = [item.opoName, item.olaName].filter(Boolean).join(" - ");
    return context ? `${context} - ${phaseText}` : phaseText;
  };

  const hasPartialComparison = sections => sections.some(section =>
    (opoDiffData?.[section] || []).some(item => {
      if (item.entityType === "OLA") {
        return !item.opoName || !item.olaName;
      }

      if (item.entityType === "PhaseLink") {
        return !item.opoName || !item.olaName || !item.phaseName;
      }

      return false;
    }));

  const buildDeletedPhaseGroups = deletedItems => {
    const phaseGroups = new Map();
    const otherItems = [];

    (deletedItems || []).forEach(item => {
      if (item.entityType !== "PhaseLink") {
        otherItems.push(item);
        return;
      }

      const groupKey = `${item.parentOpoId ?? item.opoName ?? "-"}||${item.parentOlaId ?? item.olaName ?? "-"}`;
      const existing = phaseGroups.get(groupKey) || {
        key: groupKey,
        parentOpoId: item.parentOpoId ?? null,
        parentOlaId: item.parentOlaId ?? null,
        opoName: item.opoName || "-",
        olaName: item.olaName || "-",
        phaseNames: [],
        phaseItems: [],
      };

      existing.phaseNames.push(item.phaseName || item.key || `#${item.id ?? "?"}`);
      existing.phaseItems.push(item);
      phaseGroups.set(groupKey, existing);
    });

    return {
      otherItems,
      phaseGroups: [...phaseGroups.values()].sort((a, b) => `${a.opoName} - ${a.olaName}`.localeCompare(`${b.opoName} - ${b.olaName}`)),
    };
  };

  const buildGroupedOpoDiff = diffData => {
    const deletedGroups = buildDeletedPhaseGroups(diffData?.deleted || []);
    const rootGroups = new Map();
    const updatedPhaseNamesByGroup = new Map();
    const olaFieldNames = [
      "name",
      "semester",
      "ectsCredits",
      "realECTSCredits",
      "contactQ1",
      "contactQ2",
      "contactQ3",
      "contactQ4",
      "internship",
      "internshipPercentage",
      "teachersPerClass",
      "planPerPhase",
      "zCode",
      "opoID",
    ];
    const phaseFieldNames = [
      "surplus",
      "numberOfClassesOverride",
      "semesterOverride",
      "ectsCreditsOverride",
      "realEctsCreditsOverride",
      "contactQ1Override",
      "contactQ2Override",
      "contactQ3Override",
      "contactQ4Override",
      "opoZCodeOverride",
      "olaZCodeOverride",
      "comment",
      "teachersPerClassOverride",
      "phaseID",
      "olaID",
    ];
    const currentPhaseLinksByIdentity = new Map();

    (opos || []).forEach(opo => {
      const currentOpoId = opo?.ID ?? opo?.id ?? null;
      const currentOpoName = opo?.name || "";

      (opo?.olas || []).forEach(ola => {
        const currentOlaId = ola?.ID ?? ola?.id ?? null;
        const currentOlaName = ola?.name || "";

        (ola?.phases || []).forEach(link => {
          const currentPhaseId = link?.phaseID
            ?? link?.phaseId
            ?? link?.phase?.phaseID
            ?? link?.phase?.phaseId
            ?? link?.phase?.ID
            ?? link?.phase?.id
            ?? null;
          const currentPhaseName = link?.phaseName || link?.phase?.name || "";

          const identityKeys = [
            `${currentOpoId ?? "-"}||${currentOlaId ?? "-"}||${currentPhaseId ?? "-"}`,
            `${normalizeText(currentOpoName)}||${normalizeText(currentOlaName)}||${normalizeText(currentPhaseName)}`,
          ];

          const values = {
            surplus: link?.surplus,
            numberOfClassesOverride: link?.numberOfClassesOverride,
            semesterOverride: link?.semesterOverride,
            ectsCreditsOverride: link?.ectsCreditsOverride,
            realEctsCreditsOverride: link?.realEctsCreditsOverride,
            contactQ1Override: link?.contactQ1Override,
            contactQ2Override: link?.contactQ2Override,
            contactQ3Override: link?.contactQ3Override,
            contactQ4Override: link?.contactQ4Override,
            opoZCodeOverride: link?.opoZCodeOverride,
            olaZCodeOverride: link?.olaZCodeOverride,
            comment: link?.comment,
            teachersPerClassOverride: link?.teachersPerClassOverride,
            phaseID: currentPhaseId,
            olaID: link?.olaID ?? link?.olaId ?? currentOlaId,
          };

          identityKeys.forEach(identityKey => {
            currentPhaseLinksByIdentity.set(identityKey, {
              parentOpoId: currentOpoId,
              parentOlaId: currentOlaId,
              opoName: currentOpoName,
              olaName: currentOlaName,
              phaseName: currentPhaseName,
              values,
            });
          });
        });
      });
    });

    const findCurrentPhaseLink = deletedItem => {
      const deletedPhaseId = deletedItem?.oldValues?.phaseID ?? null;
      const deletedPhaseName = deletedItem?.phaseName || "";
      const lookupKeys = [
        `${deletedItem.parentOpoId ?? "-"}||${deletedItem.parentOlaId ?? "-"}||${deletedPhaseId ?? "-"}`,
        `${normalizeText(deletedItem.opoName || "")}||${normalizeText(deletedItem.olaName || "")}||${normalizeText(deletedPhaseName)}`,
      ];

      for (const lookupKey of lookupKeys) {
        const match = currentPhaseLinksByIdentity.get(lookupKey);
        if (match) {
          return match;
        }
      }

      return null;
    };

    const convertDeletedPhaseToUpdate = deletedItem => {
      const currentPhaseLink = findCurrentPhaseLink(deletedItem);
      if (!currentPhaseLink) {
        return null;
      }

      const fieldsChanged = [];
      const oldValues = {};
      const newValues = {};

      phaseFieldNames.forEach(fieldName => {
        const oldValue = normalizeFieldValue(fieldName, deletedItem?.oldValues?.[fieldName]);
        const newValue = normalizeFieldValue(fieldName, currentPhaseLink.values?.[fieldName]);

        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          fieldsChanged.push(fieldName);
          oldValues[fieldName] = deletedItem?.oldValues?.[fieldName] ?? null;
          newValues[fieldName] = currentPhaseLink.values?.[fieldName] ?? null;
        }
      });

      if (fieldsChanged.length === 0) {
        return null;
      }

      return {
        entityType: "PhaseLink",
        id: deletedItem.id,
        key: deletedItem.key,
        parentOpoId: deletedItem.parentOpoId ?? currentPhaseLink.parentOpoId,
        parentOlaId: deletedItem.parentOlaId ?? currentPhaseLink.parentOlaId,
        opoName: deletedItem.opoName || currentPhaseLink.opoName,
        olaName: deletedItem.olaName || currentPhaseLink.olaName,
        phaseName: deletedItem.phaseName || currentPhaseLink.phaseName,
        fieldsChanged,
        oldValues,
        newValues,
      };
    };

    const addUpdatedPhaseName = (groupKey, phaseName) => {
      if (!groupKey || !phaseName) {
        return;
      }

      const current = updatedPhaseNamesByGroup.get(groupKey) || new Set();
      current.add(normalizeText(phaseName));
      updatedPhaseNamesByGroup.set(groupKey, current);
    };

    const ensureRootGroup = item => {
      const rootKey = `${item.parentOpoId ?? item.id ?? item.key ?? item.opoName ?? item.opoZCode ?? "-"}`;
      let rootGroup = rootGroups.get(rootKey);

      if (!rootGroup) {
        rootGroup = {
          key: rootKey,
          opoName: item.opoName || item.opoZCode || item.key || "-",
          opoChanges: [],
          olaGroups: new Map(),
          summaryLabels: new Set(),
        };
        rootGroups.set(rootKey, rootGroup);
      }

      if (item.opoName && rootGroup.opoName === "-") {
        rootGroup.opoName = item.opoName;
      }

      return rootGroup;
    };

    const ensureOlaGroup = (rootGroup, item, fallbackName) => {
      const olaKey = `${item.parentOlaId ?? item.id ?? item.key ?? fallbackName ?? "-"}`;
      let olaGroup = rootGroup.olaGroups.get(olaKey);

      if (!olaGroup) {
        olaGroup = {
          key: olaKey,
          olaName: item.olaName || fallbackName || item.opoName || "-",
          changes: [],
          phaseGroups: [],
          summaryLabels: new Set(),
        };
        rootGroup.olaGroups.set(olaKey, olaGroup);
      }

      if (item.olaName && olaGroup.olaName === "-") {
        olaGroup.olaName = item.olaName;
      }

      return olaGroup;
    };

    const buildLifecycleUpdateItem = (item, valueSource, mode, fallbackFieldNames) => {
      const fieldsChanged = [];
      const oldValues = {};
      const newValues = {};

      (fallbackFieldNames || []).forEach(fieldName => {
        const sourceValue = valueSource?.[fieldName];
        const normalizedValue = normalizeFieldValue(fieldName, sourceValue);

        const hasMeaningfulValue = normalizedValue !== null
          && normalizedValue !== ""
          && !(Array.isArray(normalizedValue) && normalizedValue.length === 0);

        if (!hasMeaningfulValue) {
          return;
        }

        fieldsChanged.push(fieldName);
        oldValues[fieldName] = mode === "deleted" ? sourceValue ?? null : null;
        newValues[fieldName] = mode === "created" ? sourceValue ?? null : null;
      });

      if (fieldsChanged.length === 0) {
        fieldsChanged.push("name");
        const fallbackName = item.olaName || item.phaseName || item.opoName || null;
        oldValues.name = mode === "deleted" ? fallbackName : null;
        newValues.name = mode === "created" ? fallbackName : null;
      }

      return {
        ...item,
        fieldsChanged,
        oldValues,
        newValues,
      };
    };

    (diffData?.created || []).forEach(item => {
      if (item.entityType === "OLA") {
        const rootGroup = ensureRootGroup(item);
        const olaGroup = ensureOlaGroup(rootGroup, item, item.olaName);
        olaGroup.changes.push(buildLifecycleUpdateItem(item, item.newValues, "created", olaFieldNames));
        rootGroup.summaryLabels.add("Nieuwe OLA");
        return;
      }

      if (item.entityType === "PhaseLink") {
        const rootGroup = ensureRootGroup(item);
        const olaGroup = ensureOlaGroup(rootGroup, item, item.olaName);
        olaGroup.phaseGroups.push({
          ...item,
          phaseNames: [item.phaseName || item.key || `#${item.id ?? "?"}`],
          addedPhaseGroup: true,
        });
        olaGroup.summaryLabels.add("Fases aangepast");
        rootGroup.summaryLabels.add("Fases aangepast");
      }
    });

    (diffData?.deleted || []).forEach(item => {
      if (item.entityType === "OLA") {
        const rootGroup = ensureRootGroup(item);
        const olaGroup = ensureOlaGroup(rootGroup, item, item.olaName);
        olaGroup.changes.push(buildLifecycleUpdateItem(item, item.oldValues, "deleted", olaFieldNames));
        rootGroup.summaryLabels.add("Verwijderde OLA");
      }
    });

    (diffData?.updated || []).forEach(item => {
      if (item.entityType === "OPO") {
        const rootGroup = ensureRootGroup(item);
        rootGroup.opoChanges.push(item);
        (item.fieldsChanged || []).forEach(fieldName => rootGroup.summaryLabels.add(mapDiffFieldNameToLabel(fieldName)));
        return;
      }

      if (item.entityType === "OLA") {
        const rootGroup = ensureRootGroup(item);
        const olaGroup = ensureOlaGroup(rootGroup, item, item.olaName);
        olaGroup.changes.push(item);
        (item.fieldsChanged || []).forEach(fieldName => olaGroup.summaryLabels.add(mapDiffFieldNameToLabel(fieldName)));
        return;
      }

      if (item.entityType === "PhaseLink") {
        const rootGroup = ensureRootGroup(item);
        const olaGroup = ensureOlaGroup(rootGroup, item, item.olaName);
        olaGroup.phaseGroups.push(item);
        olaGroup.summaryLabels.add("Fases aangepast");
        rootGroup.summaryLabels.add("Fases aangepast");

        const phaseGroupKey = `${item.parentOpoId ?? item.opoName ?? "-"}||${item.parentOlaId ?? item.olaName ?? "-"}`;
        addUpdatedPhaseName(phaseGroupKey, item.phaseName || item.key || "");
      }
    });

    (deletedGroups.phaseGroups || []).forEach(group => {
      const knownUpdatedPhases = updatedPhaseNamesByGroup.get(group.key);
      const unresolvedDeletedPhaseNames = [];

      const rootGroup = ensureRootGroup({
        parentOpoId: group.parentOpoId,
        opoName: group.opoName,
        opoZCode: group.opoName,
      });
      const olaGroup = ensureOlaGroup(rootGroup, {
        parentOlaId: group.parentOlaId,
        olaName: group.olaName,
      }, group.olaName);

      (group.phaseItems || []).forEach(deletedPhaseItem => {
        const deletedPhaseName = deletedPhaseItem.phaseName || deletedPhaseItem.key || `#${deletedPhaseItem.id ?? "?"}`;

        if (knownUpdatedPhases && knownUpdatedPhases.has(normalizeText(deletedPhaseName))) {
          return;
        }

        const convertedUpdate = convertDeletedPhaseToUpdate(deletedPhaseItem);
        if (convertedUpdate) {
          olaGroup.phaseGroups.push(convertedUpdate);
          olaGroup.summaryLabels.add("Fases aangepast");
          rootGroup.summaryLabels.add("Fases aangepast");
          return;
        }

        unresolvedDeletedPhaseNames.push(deletedPhaseName);
      });

      if (unresolvedDeletedPhaseNames.length > 0) {
        olaGroup.phaseGroups.push({
          ...group,
          phaseNames: unresolvedDeletedPhaseNames,
          deletedPhaseGroup: true,
        });
        olaGroup.summaryLabels.add("Fases aangepast");
        rootGroup.summaryLabels.add("Fases aangepast");
      }
    });

    const roots = [...rootGroups.values()].sort((a, b) => a.opoName.localeCompare(b.opoName));
    roots.forEach(rootGroup => {
      rootGroup.olaGroups = [...rootGroup.olaGroups.values()].sort((a, b) => a.olaName.localeCompare(b.olaName));
      const summaryParts = [...rootGroup.summaryLabels];

      rootGroup.olaGroups.forEach(olaGroup => {
        const olaLabels = [...olaGroup.summaryLabels].filter(label => label && label !== "Fases aangepast");

        if (olaLabels.length > 0) {
          summaryParts.push(olaLabels.map(label => label.toLowerCase()).join(", "));
        }

        if (olaGroup.phaseGroups.length > 0 && !olaGroup.summaryLabels.has("Fases aangepast")) {
          summaryParts.push("fases aangepast");
        }
      });

      rootGroup.summaryText = summaryParts.length > 0 ? summaryParts.join(", ") : "Aangepast";
    });

    return {
      deletedGroups,
      rootGroups: roots,
    };
  };

  const normalizeText = value => `${value ?? ""}`.trim();

  const normalizeNumber = value => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const normalizeBoolean = value => Boolean(value);

  const normalizeTags = tags => {
    if (!Array.isArray(tags)) {
      return [];
    }

    return tags
      .map(tag => {
        if (tag === null || tag === undefined) {
          return "";
        }

        if (typeof tag === "string" || typeof tag === "number") {
          return normalizeText(tag);
        }

        return normalizeText(tag.name || tag.text || "");
      })
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  };

  const normalizeFieldValue = (fieldName, value) => {
    const stringFields = ["zCode", "name", "comments", "comment", "opoZCodeOverride", "olaZCodeOverride"];
    const numberFields = [
      "programmeID",
      "semester",
      "ectsCredits",
      "realECTSCredits",
      "contactQ1",
      "contactQ2",
      "contactQ3",
      "contactQ4",
      "internshipPercentage",
      "teachersPerClass",
      "opoID",
      "phaseID",
      "olaID",
      "surplus",
      "numberOfClassesOverride",
      "semesterOverride",
      "ectsCreditsOverride",
      "realEctsCreditsOverride",
      "contactQ1Override",
      "contactQ2Override",
      "contactQ3Override",
      "contactQ4Override",
      "teachersPerClassOverride",
    ];
    const booleanFields = ["internship", "planPerPhase"];

    if (stringFields.includes(fieldName)) {
      return normalizeText(value);
    }

    if (numberFields.includes(fieldName)) {
      return normalizeNumber(value);
    }

    if (booleanFields.includes(fieldName)) {
      return normalizeBoolean(value);
    }

    if (fieldName === "tags") {
      return normalizeTags(value);
    }

    return value ?? null;
  };

  const formatError = error => {
    try {
      if (!error) return "Onbekende fout";
      if (typeof error === "string") return error;
      if (error instanceof Error) return error.message || String(error);
      if (typeof error === "object") {
        if (error.status) return `HTTP ${error.status} ${error.statusText || ""}`.trim();
        return JSON.stringify(error);
      }
      return String(error);
    } catch (e) {
      return String(error);
    }
  };

  const formatApiError = async error => {
    if (error instanceof Response) {
      const body = await error.text();
      return body || `Request failed with status ${error.status}`;
    }

    return error?.message || String(error);
  };

  const getPhaseIdentityKeys = phaseItem => {
    const targetOpoId = phaseItem?.parentOpoId ?? null;
    const targetOlaId = phaseItem?.parentOlaId ?? null;
    const targetOpoName = normalizeText(phaseItem?.opoName || "");
    const targetOlaName = normalizeText(phaseItem?.olaName || "");
    const targetPhaseId = phaseItem?.oldValues?.phaseID ?? phaseItem?.newValues?.phaseID ?? null;
    const targetPhaseName = normalizeText(phaseItem?.phaseName || "");

    return [
      `${targetOpoId ?? "-"}||${targetOlaId ?? "-"}||${targetPhaseId ?? "-"}`,
      `${targetOpoName}||${targetOlaName}||${targetPhaseName}`,
    ];
  };

  const phaseDefaultsLookup = useMemo(() => {
    const lookup = new Map();

    (opos || []).forEach(opo => {
      const opoId = opo?.ID ?? opo?.id ?? null;
      const opoName = normalizeText(opo?.name || "");

      (opo?.olas || []).forEach(ola => {
        const olaId = ola?.ID ?? ola?.id ?? null;
        const olaName = normalizeText(ola?.name || "");

        (ola?.phases || []).forEach(link => {
          const phaseId = link?.phaseID
            ?? link?.phaseId
            ?? link?.phase?.phaseID
            ?? link?.phase?.phaseId
            ?? link?.phase?.ID
            ?? link?.phase?.id
            ?? null;
          const phaseName = normalizeText(link?.phaseName || link?.phase?.name || "");

          const defaults = {
            surplus: 0,
            numberOfClassesOverride: link?.phase?.numberOfClasses ?? link?.numberOfClasses ?? ola?.teachersPerClass ?? null,
            semesterOverride: ola?.semester ?? null,
            ectsCreditsOverride: ola?.ectsCredits ?? null,
            realEctsCreditsOverride: ola?.realECTSCredits ?? null,
            contactQ1Override: ola?.contactQ1 ?? null,
            contactQ2Override: ola?.contactQ2 ?? null,
            contactQ3Override: ola?.contactQ3 ?? null,
            contactQ4Override: ola?.contactQ4 ?? null,
            opoZCodeOverride: opo?.zCode ?? opo?.z_code ?? null,
            olaZCodeOverride: ola?.zCode ?? ola?.z_code ?? null,
            comment: null,
            teachersPerClassOverride: ola?.teachersPerClass ?? null,
          };

          const identityKeys = [
            `${opoId ?? "-"}||${olaId ?? "-"}||${phaseId ?? "-"}`,
            `${opoName}||${olaName}||${phaseName}`,
          ];

          identityKeys.forEach(identityKey => {
            if (!lookup.has(identityKey)) {
              lookup.set(identityKey, defaults);
            }
          });
        });
      });
    });

    return lookup;
  }, [opos]);

  const getPhaseDefaultValues = phaseItem => {
    const identityKeys = getPhaseIdentityKeys(phaseItem);

    for (const identityKey of identityKeys) {
      const defaults = phaseDefaultsLookup.get(identityKey);
      if (defaults) {
        return defaults;
      }
    }

    return {
      surplus: 0,
      numberOfClassesOverride: null,
      semesterOverride: null,
      ectsCreditsOverride: null,
      realEctsCreditsOverride: null,
      contactQ1Override: null,
      contactQ2Override: null,
      contactQ3Override: null,
      contactQ4Override: null,
      opoZCodeOverride: null,
      olaZCodeOverride: null,
      comment: null,
      teachersPerClassOverride: null,
    };
  };

  const formatPhaseDiffValue = (phaseItem, fieldName, value) => {
    if (value !== null && value !== undefined && value !== "") {
      return formatDiffValue(value);
    }

    const defaults = getPhaseDefaultValues(phaseItem);
    const fallbackValue = defaults[fieldName];

    if (fallbackValue === null || fallbackValue === undefined || fallbackValue === "") {
      return formatDiffValue(value);
    }

    return formatDiffValue(fallbackValue);
  };

  const buildLocalOpoDiff = (currentOposInput, snapshotOposInput) => {
    const toFlatData = opoList => {
      const opoItems = [];
      const olaItems = [];
      const phaseLinkItems = [];

      (opoList || []).forEach(opo => {
        const opoID = opo?.ID ?? opo?.id ?? null;
        const programmeID = opo?.programmeID ?? opo?.programmeId ?? opo?.programme?.ID ?? opo?.programme?.id ?? null;
        const opoName = opo?.name || "";
        const opoZCode = opo?.zCode || opo?.z_code || "";
        const normalizedOpoCode = normalizeText(opoZCode);
        const opoIdentity = normalizedOpoCode || normalizeText(opoName);
        const opoKey = `${programmeID ?? "-"}|${opoIdentity}`;

        opoItems.push({
          entityType: "OPO",
          id: opoID,
          key: opoKey,
          opoName,
          opoZCode,
          values: {
            zCode: opoZCode,
            name: opoName,
            comments: opo?.comments,
            programmeID,
            tags: opo?.tags || opo?.opoTags || [],
          },
        });

        (opo?.olas || []).forEach(ola => {
          const olaID = ola?.ID ?? ola?.id ?? null;
          const olaName = ola?.name || "";
          const olaZCode = ola?.zCode || ola?.z_code || "";
          const olaOpoID = ola?.opoID ?? ola?.opoId ?? opoID ?? null;
          const normalizedOlaCode = normalizeText(olaZCode);
          const olaIdentity = normalizedOlaCode || normalizeText(olaName);
          const olaKey = `${olaOpoID ?? opoKey}|${olaIdentity}`;

          olaItems.push({
            entityType: "OLA",
            id: olaID,
            key: olaKey,
            parentOpoId: opoID,
            opoName,
            opoZCode,
            olaName,
            values: {
              name: olaName,
              semester: ola?.semester,
              ectsCredits: ola?.ectsCredits,
              realECTSCredits: ola?.realECTSCredits,
              contactQ1: ola?.contactQ1,
              contactQ2: ola?.contactQ2,
              contactQ3: ola?.contactQ3,
              contactQ4: ola?.contactQ4,
              internship: ola?.internship,
              internshipPercentage: ola?.internshipPercentage,
              teachersPerClass: ola?.teachersPerClass,
              planPerPhase: ola?.planPerPhase,
              zCode: olaZCode,
              opoID: olaOpoID,
            },
          });

          (ola?.phases || []).forEach(link => {
            const phaseLinkID = link?.ID ?? link?.id ?? null;
            const phaseID = link?.phaseID
              ?? link?.phaseId
              ?? link?.phase?.phaseID
              ?? link?.phase?.phaseId
              ?? link?.phase?.ID
              ?? link?.phase?.id
              ?? null;
            const phaseName = link?.phaseName || link?.phase?.name || "";
            const phaseOlaID = link?.olaID ?? link?.olaId ?? link?.ola?.ID ?? link?.ola?.id ?? olaID ?? null;
            const normalizedPhaseName = normalizeText(phaseName);
            const phaseIdentityFallback = normalizedPhaseName || (phaseLinkID !== null && phaseLinkID !== undefined ? `link-${phaseLinkID}` : "-");
            const phaseIdentity = phaseID ?? phaseIdentityFallback;
            const phaseKey = `${phaseOlaID ?? olaKey}|${phaseIdentity}`;

            phaseLinkItems.push({
              entityType: "PhaseLink",
              id: phaseLinkID,
              key: phaseKey,
              parentOpoId: opoID,
              parentOlaId: olaID,
              opoName,
              opoZCode,
              olaName,
              phaseName,
              values: {
                surplus: link?.surplus,
                numberOfClassesOverride: link?.numberOfClassesOverride,
                semesterOverride: link?.semesterOverride,
                ectsCreditsOverride: link?.ectsCreditsOverride,
                realEctsCreditsOverride: link?.realEctsCreditsOverride,
                contactQ1Override: link?.contactQ1Override,
                contactQ2Override: link?.contactQ2Override,
                contactQ3Override: link?.contactQ3Override,
                contactQ4Override: link?.contactQ4Override,
                opoZCodeOverride: link?.opoZCodeOverride,
                olaZCodeOverride: link?.olaZCodeOverride,
                comment: link?.comment,
                teachersPerClassOverride: link?.teachersPerClassOverride,
                phaseID,
                olaID: phaseOlaID,
              },
            });
          });
        });
      });

      return { opoItems, olaItems, phaseLinkItems };
    };

    const buildEntityDiff = (currentItems, snapshotItems, fieldsToCompare) => {
      const currentLeft = [...currentItems];
      const snapshotLeft = [...snapshotItems];
      const matchedPairs = [];

      const takeByIdMatch = () => {
        const currentIndex = currentLeft.findIndex(item => (
          item.id !== null
          && item.id !== undefined
          && snapshotLeft.some(snapshotItem => `${snapshotItem.id}` === `${item.id}`)
        ));
        if (currentIndex === -1) {
          return false;
        }

        const currentItem = currentLeft[currentIndex];
        const snapshotIndex = snapshotLeft.findIndex(item => `${item.id}` === `${currentItem.id}`);
        if (snapshotIndex === -1) {
          return false;
        }

        const snapshotItem = snapshotLeft[snapshotIndex];
        currentLeft.splice(currentIndex, 1);
        snapshotLeft.splice(snapshotIndex, 1);
        matchedPairs.push([currentItem, snapshotItem]);
        return true;
      };

      const takeByKeyMatch = () => {
        const currentIndex = currentLeft.findIndex(item => (
          !!item.key
          && snapshotLeft.some(snapshotItem => snapshotItem.key === item.key)
        ));
        if (currentIndex === -1) {
          return false;
        }

        const currentItem = currentLeft[currentIndex];
        const snapshotIndex = snapshotLeft.findIndex(item => item.key === currentItem.key);
        if (snapshotIndex === -1) {
          return false;
        }

        const snapshotItem = snapshotLeft[snapshotIndex];
        currentLeft.splice(currentIndex, 1);
        snapshotLeft.splice(snapshotIndex, 1);
        matchedPairs.push([currentItem, snapshotItem]);
        return true;
      };

      while (takeByIdMatch());
      while (takeByKeyMatch());

      const created = currentLeft.map(item => ({
        entityType: item.entityType,
        id: item.id,
        key: item.key,
        parentOpoId: item.parentOpoId,
        parentOlaId: item.parentOlaId,
        opoName: item.opoName,
        opoZCode: item.opoZCode,
        olaName: item.olaName,
        phaseName: item.phaseName,
        newValues: item.values,
      }));

      const deleted = snapshotLeft.map(item => ({
        entityType: item.entityType,
        id: item.id,
        key: item.key,
        parentOpoId: item.parentOpoId,
        parentOlaId: item.parentOlaId,
        opoName: item.opoName,
        opoZCode: item.opoZCode,
        olaName: item.olaName,
        phaseName: item.phaseName,
        oldValues: item.values,
      }));

      const updated = matchedPairs.flatMap(([currentItem, snapshotItem]) => {
        const fieldsChanged = [];
        const oldValues = {};
        const newValues = {};

        fieldsToCompare.forEach(fieldName => {
          const oldValue = normalizeFieldValue(fieldName, snapshotItem.values?.[fieldName]);
          const newValue = normalizeFieldValue(fieldName, currentItem.values?.[fieldName]);

          if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            fieldsChanged.push(fieldName);
            oldValues[fieldName] = snapshotItem.values?.[fieldName] ?? null;
            newValues[fieldName] = currentItem.values?.[fieldName] ?? null;
          }
        });

        if (fieldsChanged.length === 0) {
          return [];
        }

        return [{
          entityType: currentItem.entityType,
          id: currentItem.id ?? snapshotItem.id,
          key: currentItem.key || snapshotItem.key,
          parentOpoId: currentItem.parentOpoId ?? snapshotItem.parentOpoId,
          parentOlaId: currentItem.parentOlaId ?? snapshotItem.parentOlaId,
          opoName: currentItem.opoName || snapshotItem.opoName,
          opoZCode: currentItem.opoZCode || snapshotItem.opoZCode,
          olaName: currentItem.olaName || snapshotItem.olaName,
          phaseName: currentItem.phaseName || snapshotItem.phaseName,
          fieldsChanged,
          oldValues,
          newValues,
        }];
      });

      return { created, deleted, updated };
    };

    const currentFlat = toFlatData(currentOposInput);
    const snapshotFlat = toFlatData(snapshotOposInput);

    const opoFields = ["zCode", "name", "comments", "programmeID", "tags"];
    const olaFields = [
      "name",
      "semester",
      "ectsCredits",
      "realECTSCredits",
      "contactQ1",
      "contactQ2",
      "contactQ3",
      "contactQ4",
      "internship",
      "internshipPercentage",
      "teachersPerClass",
      "planPerPhase",
      "zCode",
      "opoID",
    ];
    const phaseFields = [
      "surplus",
      "numberOfClassesOverride",
      "semesterOverride",
      "ectsCreditsOverride",
      "realEctsCreditsOverride",
      "contactQ1Override",
      "contactQ2Override",
      "contactQ3Override",
      "contactQ4Override",
      "opoZCodeOverride",
      "olaZCodeOverride",
      "comment",
      "teachersPerClassOverride",
      "phaseID",
      "olaID",
    ];

    const opoDiff = buildEntityDiff(currentFlat.opoItems, snapshotFlat.opoItems, opoFields);
    const olaDiff = buildEntityDiff(currentFlat.olaItems, snapshotFlat.olaItems, olaFields);
    const phaseDiff = buildEntityDiff(currentFlat.phaseLinkItems, snapshotFlat.phaseLinkItems, phaseFields);

    const created = [...opoDiff.created, ...olaDiff.created, ...phaseDiff.created];
    const deleted = [...opoDiff.deleted, ...olaDiff.deleted, ...phaseDiff.deleted];
    const updated = [...opoDiff.updated, ...olaDiff.updated, ...phaseDiff.updated];

    return {
      created,
      deleted,
      updated,
      summary: {
        created: created.length,
        updated: updated.length,
        deleted: deleted.length,
      },
    };
  };

  const mergeMissingTagChanges = (apiDiff, currentOpos, snapshotOpos) => {
    if (!apiDiff) {
      return apiDiff;
    }

    const localDiff = buildLocalOpoDiff(currentOpos || [], snapshotOpos || []);
    const localTagUpdates = (localDiff.updated || []).filter(item =>
      item.entityType === "OPO" && (item.fieldsChanged || []).includes("tags"));

    if (localTagUpdates.length === 0) {
      return apiDiff;
    }

    const mergedUpdated = [...(apiDiff.updated || [])];

    localTagUpdates.forEach(localItem => {
      const existing = mergedUpdated.find(item =>
        item.entityType === "OPO"
        && ((item.id !== undefined && item.id !== null && localItem.id !== undefined && localItem.id !== null && `${item.id}` === `${localItem.id}`)
          || (item.key && localItem.key && `${item.key}` === `${localItem.key}`)));

      if (!existing) {
        mergedUpdated.push(localItem);
        return;
      }

      const fields = new Set(existing.fieldsChanged || []);
      if (!fields.has("tags")) {
        fields.add("tags");
        existing.fieldsChanged = [...fields];
        existing.oldValues = {
          ...(existing.oldValues || {}),
          tags: localItem.oldValues?.tags || [],
        };
        existing.newValues = {
          ...(existing.newValues || {}),
          tags: localItem.newValues?.tags || [],
        };
      }
    });

    return {
      ...apiDiff,
      updated: mergedUpdated,
      summary: {
        ...(apiDiff.summary || {}),
        created: (apiDiff.created || []).length,
        updated: mergedUpdated.length,
        deleted: (apiDiff.deleted || []).length,
      },
    };
  };

  const runDiffWithSnapshot = async (selectedSnapshot, showErrorToast = true, showLoadingIndicator = true) => {
    if (showLoadingIndicator) {
      setRunningDiff(true);
    }

    try {
      const [diffResult, currentOposResult, snapshotOposResult] = await Promise.allSettled([
        api.getOposDiff(selectedSnapshot),
        api.getOPOs(null),
        api.getOPOs(selectedSnapshot),
      ]);

      const currentOpos = currentOposResult.status === "fulfilled" ? currentOposResult.value || [] : [];
      const snapshotOpos = snapshotOposResult.status === "fulfilled" ? snapshotOposResult.value || [] : [];

      let result;

      if (diffResult.status === "fulfilled") {
        result = mergeMissingTagChanges(diffResult.value, currentOpos, snapshotOpos);
      } else {
        const error = diffResult.reason;

        if (handleAuthFailure(error)) {
          return false;
        }

        if (error?.status === 404) {
          result = buildLocalOpoDiff(currentOpos, snapshotOpos);
        } else {
          throw error;
        }
      }

      if (!result) {
        throw new Error("Geen diffresultaat beschikbaar.");
      }

      setOpoDiffData(result);
      localStorage.setItem(OPO_DIFF_SNAPSHOT_STORAGE_KEY, selectedSnapshot);
      return true;
    } catch (error) {
      if (showErrorToast) {
        setOpoDiffData(null);
        const msg = await formatApiError(error);
        toastService.send({ title: "Vergelijking mislukt", message: msg });
      }
      return false;
    } finally {
      if (showLoadingIndicator) {
        setRunningDiff(false);
      }
    }
  };

  const runDiff = async () => {
    const selectedSnapshot = diffSnapshotName.trim();

    if (!selectedSnapshot) {
      toastService.send({ title: "Snapshot vereist", message: "Vul een snapshotnaam in." });
      return;
    }

    try {
      const exists = await snapshotExists(selectedSnapshot);
      if (!exists) {
        setOpoDiffData(null);
        setDiffOpenState(false);
        toastService.send({ title: "Snapshot bestaat niet", message: "De ingevulde snapshot bestaat niet." });
        return;
      }
    } catch (error) {
      toastService.send({ title: "Snapshotcontrole mislukt", message: formatError(error) });
      return;
    }

    const succeeded = await runDiffWithSnapshot(selectedSnapshot, true, true);
    if (succeeded) {
      setDiffOpenState(true);
    }
  };

  const refreshDiffAfterDataChange = async () => {
    const selectedSnapshot = diffSnapshotName.trim();
    if (!selectedSnapshot || !opoDiffData) {
      return;
    }

    await runDiffWithSnapshot(selectedSnapshot, false, false);
  };

  const loadOPOs = async (setLoad = true) => {
    try {
      if (setLoad) {
        setLoading(true);
      }
      const response = await api.getProgrammes(snapshot);
      setProgrammes(sortProgrammesLikeOpleidingenPage(response, isShownInOpleidingenFilter));

      const classResponse = await api.getOPOs(snapshot);
      classResponse.sort((a, b) => a.programme.name.localeCompare(b.programme.name) || a.name.localeCompare(b.name));
      setOPOs(classResponse);

      // check if SAT-PersonalProgrammeFilter exists in local storage - if not: create it to avoid null reference
      if (!localStorage.getItem("SAT-PersonalProgrammeFilter")) {
        localStorage.setItem("SAT-PersonalProgrammeFilter", JSON.stringify([]));
      }

      let personalProgrammeIDs = [];
      JSON.parse(localStorage.getItem("SAT-PersonalProgrammeFilter")).forEach(selectedProgramme => {
        personalProgrammeIDs = [...personalProgrammeIDs, selectedProgramme.ID];
      });
      setSelectedProgrammeIDs(personalProgrammeIDs);

      let currentParallels = [];
      if (!snapshot) { // TEMPORARY WORKAROUND - old back-end always returns current parallels instead of parallels from snapshot
        currentParallels = await api.getParallels(snapshot);
        currentParallels.sort((a, b) => (b.mainOLA.name.localeCompare(a.mainOLA.name) || a.level - b.level) ? 1 : -1);
      }
      setParallels(currentParallels);

      const assignmentsFromAPI = await api.getAssignments(snapshot);
      setAssignments(assignmentsFromAPI);

      if (setLoad) {
        setLoading(false);
      }
      return true;
    } catch (error) {
      toastService.send({ title: "An error occured", message: formatError(error) });
      if (setLoad) {
        setLoading(false);
      }
      return false;
    }
  };

  const openModal = data => {
    setModal(
      <EditModal
        hideModal={async () => {
          setModal(null);
          await loadOPOs(false);
          await refreshDiffAfterDataChange();
        }}
        data={data}
        parallels={parallels}
        assignments={assignments}
      />,
    );
  };

  const openImportModal = () => {
    setModal(
      <ImportModal
        hideModal={async () => {
          setModal(null);
          await loadOPOs(false);
          await refreshDiffAfterDataChange();
        }}
      />,
    );
  };

  const openRemoveModal = opo => {
    setRemoveModal(
      <RemoveModal
        hideModal={async () => {
          setRemoveModal(null);
          await loadOPOs();
          await refreshDiffAfterDataChange();
        }}
        data={opo}
      />,
    );
  };

  const exportData = async () => {
    try {
      const { fetchParamCreator, fetch } = getFetchParamGenerator();
      const opts = fetchParamCreator.getAssignmentsExport(snapshot, {});
      const resp = await fetch(getApiURL() + opts.url, opts.options);
      // download content as a file in new tab
      const file = await resp.blob();

      downloadBlob(file, "opos.xlsx");

    } catch (resp) {
      console.log(resp);
    }
  };

  useEffect(() => {
    const savedSnapshotName = localStorage.getItem(OPO_DIFF_SNAPSHOT_STORAGE_KEY) || "";
    const savedOpenState = localStorage.getItem(OPO_DIFF_OPEN_STORAGE_KEY);
    const shouldOpenDiff = savedOpenState === "true";

    setDiffSnapshotName(savedSnapshotName);
    setIsDiffOpen(shouldOpenDiff);

    const initializePage = async () => {
      setLoading(true);

      const [loaded] = await Promise.all([
        loadOPOs(false),
        loadSnapshots(),
      ]);

      if (loaded && savedSnapshotName.trim()) {
        try {
          if (await snapshotExists(savedSnapshotName.trim())) {
            await runDiffWithSnapshot(savedSnapshotName.trim(), false, false);
          } else {
            clearSelectedSnapshotName();
            setDiffOpenState(false);
          }
        } catch {
          // Keep the page usable even if diff initialization fails.
        }
      }

      setLoading(false);
    };

    initializePage();
  }, [snapshot]);

  const toggleSelectedProgrammeFiler = (id, checked) => {
    if (checked) {
      setSelectedProgrammeIDs([...selectedProgrammeIDs, id]);
    } else {
      setSelectedProgrammeIDs(selectedProgrammeIDs.filter(i => i !== id));
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <i className="fad fa-spinner-third fa-spin fa-5x" />
      </div>
    );
  }
  return (
    <Container>
      {modal}
      {removeModal}
      <Row>
        <h1>OPO&apos;s</h1>
      </Row>
      <Row className="my-2">
        <Col className="d-flex justify-content-end align-items-start">
          <InputGroup style={{ maxWidth: "380px", marginInlineEnd: "1rem" }}>
            <Form.Control
              type="text"
              list={diffSnapshotName ? "opo-diff-snapshot-options" : undefined}
              value={diffSnapshotName}
              onChange={e => setSelectedSnapshotName(e.target.value)}
              placeholder="Zoek snapshot"
              autoComplete="off"
            />
            <datalist id="opo-diff-snapshot-options">
              {availableSnapshots.map(snapshotName => (
                <option key={snapshotName} value={snapshotName} />
              ))}
            </datalist>
            <Form.Select
              aria-label="Selecteer snapshot"
              value={diffSnapshotName}
              onChange={async e => {
                const selectedValue = e.target.value;
                setSelectedSnapshotName(selectedValue);
                const succeeded = await runDiffWithSnapshot(selectedValue, true, true);
                if (succeeded) {
                  setDiffOpenState(true);
                }
              }}
              className="flex-grow-0"
              style={{ width: "44px", paddingInline: "0.4rem", backgroundPosition: "center", textIndent: "-9999px", overflow: "hidden" }}
            >
              {availableSnapshots.map(snapshotName => (
                <option key={snapshotName} value={snapshotName}>{snapshotName}</option>
              ))}
            </Form.Select>
          </InputGroup>
          <Button variant="primary" onClick={runDiff} disabled={runningDiff} style={{ marginInlineEnd: "1rem" }}>
            {runningDiff ? (
              <>
                <i className="fad fa-spinner-third fa-spin" />
                &nbsp;Vergelijken...
              </>
            ) : (
              <>
                <i className="far fa-code-compare" />
                &nbsp;Vergelijk OPO&apos;s
              </>
            )}
          </Button>
          <Button variant="secondary" onClick={() => exportData()}>
            <i className="far fa-file-export" /> Exporteren
          </Button>
          &nbsp;
          <Button variant="secondary" onClick={() => openImportModal()}>
            <i className="far fa-file-excel" />
            &nbsp;Importeren
          </Button>
          &nbsp;
          <Button variant="success" onClick={() => openModal()}>
            <i className="far fa-plus" />
            &nbsp;Nieuw
          </Button>
        </Col>
      </Row>

      {opoDiffData && isDiffOpen && (
        <Row className="mb-3">
          <Col>
            <div className="border rounded p-3 bg-light">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <h5 className="mb-0">OPO-verschillen</h5>
                <div className="d-flex align-items-center gap-3">
                  <Button variant="link" className="p-0 text-decoration-none" onClick={collapseAllDiffDetails}>
                    Alles inklappen
                  </Button>
                  <Button variant="link" className="p-0 text-decoration-none" onClick={() => setDiffOpenState(false)}>
                    Sluiten
                  </Button>
                </div>
              </div>

              {hasPartialComparison(["created", "updated", "deleted"]) && (
                <p className="mb-2 text-warning">
                  <strong>Opgelet:</strong> vergelijking lijkt onvolledig. Sommige contextgegevens ontbreken.
                </p>
              )}

                          {(() => {
              const createdOpos = (opoDiffData.created || []).filter(item => item.entityType === "OPO");
              const deletedOpos = (opoDiffData.deleted || []).filter(item => item.entityType === "OPO");
              const groupedDiff = buildGroupedOpoDiff(opoDiffData);
              return (
                <>
                  <p className="mb-2">
                    <strong>Aangemaakt:</strong> {createdOpos.length} | <strong>Aangepast:</strong> {groupedDiff.rootGroups.length} | <strong>Verwijderd:</strong> {deletedOpos.length}
                  </p>
                  <p className="mb-2">
                    <strong>Aangemaakt:</strong>{" "}
                    {createdOpos.length > 0 ? (
                      createdOpos.map((item, index, array) => (
                        <span key={`${item.entityType}-${item.id ?? item.key ?? index}`}>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 align-baseline text-decoration-none"
                            onClick={() => jumpToOpoRow(item.id ?? item.opoID ?? item.parentOpoId)}
                          >
                            {buildDiffItemDisplayName(item)}
                          </Button>
                          {index < array.length - 1 ? ", " : ""}
                        </span>
                      ))
                    ) : "-"}
                  </p>
                  <p className="mb-2">
                    <strong>Verwijderd:</strong>{" "}
                    {deletedOpos.length > 0 ? deletedOpos.map(item => buildDiffItemDisplayName(item)).join(", ") : "-"}
                  </p>
                </>
              );
            })()}
              <div ref={diffDetailsContainerRef}>
                {(() => {
                  const groupedDiff = buildGroupedOpoDiff(opoDiffData);
                  return (
                    <>
                      <strong>Aangepast</strong>
                      {groupedDiff.rootGroups.length > 0 ? (
                        <ul className="mb-0 mt-2 list-unstyled">
                          {groupedDiff.rootGroups.map(rootGroup => (
                            <li key={rootGroup.key} className="mb-2">
                              <details className="border rounded bg-white px-2 py-1">
                                <summary className="fw-semibold">
                                  {rootGroup.opoName}
                                  <span className="text-muted fw-normal">: {rootGroup.summaryText || "Aangepast"}</span>
                                </summary>
                                <div className="mt-2 small">
                                  {rootGroup.opoChanges.length > 0 && (
                                    <div className="mb-3">
                                      <strong>OPO</strong>
                                      <ul className="list-unstyled mb-0 mt-2">
                                        {rootGroup.opoChanges.map((item, index) => {
                                          const changedFields = [...new Set(item.fieldsChanged || [])]
                                            .sort((a, b) => mapDiffFieldNameToLabel(a).localeCompare(mapDiffFieldNameToLabel(b)));

                                          return (
                                            <li key={`${item.entityType}-${item.id ?? item.key ?? index}`} className="border rounded bg-white px-2 py-1 mb-2">
                                              <div className="d-flex gap-3 flex-wrap mb-2">
                                                <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => jumpToOpoRow(item.id ?? item.ID ?? item.opoID)}>
                                                  Ga naar rij
                                                </Button>
                                                <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => openEditModalFromDiff(item.id ?? item.ID ?? item.opoID)}>
                                                  Bewerken
                                                </Button>
                                              </div>
                                              <ul className="list-unstyled mb-0 mt-2">
                                                {changedFields.map((fieldName, index) => (
                                                  <li key={`${item.entityType}-${item.id ?? item.key}-${fieldName}`} className={`d-flex py-1 align-items-start${index > 0 ? " border-top" : ""}`}>
                                                    <span className="fw-semibold" style={{ minWidth: "220px" }}>{mapDiffFieldNameToLabel(fieldName)}</span>
                                                    <span className="me-3" style={{ minWidth: "260px", color: "#b00020" }}>
                                                      {formatDiffFieldValue(fieldName, item.oldValues?.[fieldName], item)}
                                                    </span>
                                                    <span className="px-2">→</span>
                                                    <span className="me-3" style={{ color: "#1b5e20", fontWeight: 600 }}>
                                                      {formatDiffFieldValue(fieldName, item.newValues?.[fieldName], item)}
                                                    </span>
                                                  </li>
                                                ))}
                                              </ul>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  )}

                                  {rootGroup.olaGroups.length > 0 && (
                                    <div>
                                      <strong>OLA</strong>
                                      <ul className="list-unstyled mb-0 mt-2">
                                        {rootGroup.olaGroups.map(olaGroup => (
                                          <li key={olaGroup.key} className="border rounded bg-white px-2 py-1 mb-2">
                                            <div className="fw-semibold">{olaGroup.olaName}</div>
                                            {olaGroup.changes.length > 0 && (
                                              <ul className="list-unstyled mb-0 mt-2">
                                                {olaGroup.changes.map((item, index) => {
                                                  if (item.lifecycleAction === "created" || item.lifecycleAction === "deleted") {
                                                    return (
                                                      <li key={`${item.entityType}-${item.id ?? item.key ?? index}`} className="border rounded bg-white px-2 py-1 mb-2">
                                                        <div className="fw-semibold">
                                                          {item.lifecycleAction === "created" ? "Nieuwe OLA" : "Verwijderde OLA"}: {item.olaName || item.key || "-"}
                                                        </div>
                                                      </li>
                                                    );
                                                  }

                                                  const changedFields = [...new Set(item.fieldsChanged || [])]
                                                    .sort((a, b) => mapDiffFieldNameToLabel(a).localeCompare(mapDiffFieldNameToLabel(b)));

                                                  return (
                                                    <li key={`${item.entityType}-${item.id ?? item.key ?? index}`} className="border rounded bg-white px-2 py-1 mb-2">
                                                      <ul className="list-unstyled mb-0 mt-2">
                                                        {changedFields.map((fieldName, index) => (
                                                          <li key={`${item.entityType}-${item.id ?? item.key}-${fieldName}`} className={`d-flex py-1 align-items-start${index > 0 ? " border-top" : ""}`}>
                                                            <span className="fw-semibold" style={{ minWidth: "220px" }}>{mapDiffFieldNameToLabel(fieldName)}</span>
                                                            <span className="me-3" style={{ minWidth: "260px", color: "#b00020" }}>
                                                              {formatDiffFieldValue(fieldName, item.oldValues?.[fieldName], item)}
                                                            </span>
                                                            <span className="px-2">→</span>
                                                            <span className="me-3" style={{ color: "#1b5e20", fontWeight: 600 }}>
                                                              {formatDiffFieldValue(fieldName, item.newValues?.[fieldName], item)}
                                                            </span>
                                                          </li>
                                                        ))}
                                                      </ul>
                                                    </li>
                                                  );
                                                })}
                                              </ul>
                                            )}

                                            {olaGroup.phaseGroups.length > 0 && (
                                              <div className="mt-3">
                                                <strong className="fw-semibold d-block mb-2">Fases aangepast</strong>
                                                <ul className="list-unstyled mb-0 mt-2">
                                                  {olaGroup.phaseGroups.map(group => {
                                                    if (group.deletedPhaseGroup) {
                                                      return (
                                                        <li key={group.key} className="border rounded bg-white px-2 py-1 mb-2">
                                                          <div className="fw-semibold">
                                                            Niet meer gekoppeld: {(group.phaseNames || [group.phaseName || group.key || "-"]).join(", ")}
                                                          </div>
                                                        </li>
                                                      );
                                                    }

                                                    if (group.addedPhaseGroup) {
                                                      return (
                                                        <li key={group.key || `${group.phaseName}-${group.id ?? "-"}`} className="border rounded bg-white px-2 py-1 mb-2">
                                                          <div className="fw-semibold">
                                                            Nieuw gekoppeld: {(group.phaseNames || [group.phaseName || group.key || "-"]).join(", ")}
                                                          </div>
                                                        </li>
                                                      );
                                                    }

                                                    const changedFields = [...new Set(group.fieldsChanged || [])]
                                                      .sort((a, b) => mapDiffFieldNameToLabel(a).localeCompare(mapDiffFieldNameToLabel(b)));

                                                    return (
                                                      <li key={group.key || `${group.phaseName}-${group.id ?? "-"}`} className="border rounded bg-white px-2 py-1 mb-2">
                                                        <div className="fw-semibold mb-1">
                                                          Fase: {group.phaseName || "-"}
                                                        </div>
                                                        <div className="text-muted small mb-2">
                                                          {group.opoName} - {group.olaName}
                                                        </div>
                                                        <ul className="list-unstyled mb-0 mt-2">
                                                          {changedFields.map((fieldName, index) => (
                                                            <li key={`${group.key || group.id}-${fieldName}`} className={`d-flex py-1 align-items-start${index > 0 ? " border-top" : ""}`}>
                                                              <span className="fw-semibold" style={{ minWidth: "220px" }}>{mapDiffFieldNameToLabel(fieldName)}</span>
                                                              <span className="me-3" style={{ minWidth: "260px", color: "#b00020" }}>
                                                                {formatPhaseDiffValue(group, fieldName, group.oldValues?.[fieldName])}
                                                              </span>
                                                              <span className="px-2">→</span>
                                                              <span className="me-3" style={{ color: "#1b5e20", fontWeight: 600 }}>
                                                                {formatPhaseDiffValue(group, fieldName, group.newValues?.[fieldName])}
                                                              </span>
                                                            </li>
                                                          ))}
                                                        </ul>
                                                      </li>
                                                    );
                                                  })}
                                                </ul>
                                              </div>
                                            )}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </details>
                            </li>
                          ))}

                        </ul>
                      ) : (
                        <span>&nbsp;-</span>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </Col>
        </Row>
      )}

      <Row>
        <Col md={2}>
          <h3>Filter</h3>
          <hr />
          <h4>Opleiding</h4>
          <ul className="list-group">
            {programmes.map(programme => (
              <li key={programme.ID} className="list-group-item text-break">
                <Form.Check
                  inline
                  label={programme.name}
                  id={programme.ID}
                  type="checkbox"
                  checked={selectedProgrammeIDs.includes(programme.ID)}
                  onChange={e => toggleSelectedProgrammeFiler(programme.ID, e.target.checked)}
                />
              </li>
            ))}
          </ul>
        </Col>
        <Col md={10}>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Z-Code</th>
                <th>Naam</th>
                <th>Fases</th>
                <th className="text-transparent">&nbsp;.</th>
              </tr>
            </thead>
            <tbody>
              {opos.map(c => (
                <tr
                  key={c.ID}
                  ref={el => {
                    if (el) {
                      opoRowRefs.current[`${c.ID}`] = el;
                    }
                  }}
                  className={`${selectedProgrammeIDs.includes(c.programmeID) || selectedProgrammeIDs.length === 0 ? "" : "d-none"} ${highlightedOpoID === `${c.ID}` ? "table-active" : ""}`.trim()}
                >
                  <td>{c.zCode}</td>
                  <td>
                    {c.name}&nbsp;
                    {c.comments !== "" && (
                      <span className="text-info">
                        <i className="far fa-comment-dots" />
                      </span>
                    )}
                  </td>
                  <td>
                    <ul className="list-group">
                      {c.olas.map(ola =>
                        ola.phases.map(phase => (
                          <li className="list-group-item d-flex justify-content-between align-items-center">
                            {phase.phase.name} <span className="badge-primary badge-pill">{phase.phase.numberOfStudents + phase.surplus}</span>
                          </li>
                        )),
                      )}
                    </ul>
                  </td>
                  <td className="text-end">
                    <Button variant="info" onClick={() => openModal(c)}>
                      <i className="far fa-edit" />
                    </Button>{" "}
                    <Button variant="danger" onClick={() => openRemoveModal(c)} disabled={snapshot}>
                      <i className="far fa-trash" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
}

export default OPOs;
